import React, { useEffect, useMemo, useState } from 'react';
import { fetchDashboardStats } from '../services/api';
import { DashboardStats, User } from '../types';
import DrilldownModal from '../components/Dashboard/DrilldownModal';

const fmt = (n: number) => `₹${Number(n).toLocaleString('en-IN')}`;

const Delta: React.FC<{ val: number; invert?: boolean; suffix?: string }> = ({ val, invert, suffix }) => {
  const positive = invert ? val < 0 : val >= 0;
  return (
    <span className={`kpi-delta ${positive ? 'kpi-delta-up' : 'kpi-delta-down'}`}>
      {val >= 0 ? '▲' : '▼'} {Math.abs(val)}% {suffix ?? 'vs prior period'}
    </span>
  );
};

function DonutChart({ data, colors }: { data: Record<string, number>; colors: Record<string, string> }) {
  const total  = Object.values(data).reduce((a, b) => a + b, 0) || 1;
  let offset   = 0;
  const r      = 40;
  const circ   = 2 * Math.PI * r;
  const segments = Object.entries(data).map(([k, v]) => {
    const pct   = v / total;
    const dash  = pct * circ;
    const seg   = { key: k, value: v, pct, dash, offset };
    offset += dash;
    return seg;
  });

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#F0F0F0" strokeWidth="16" />
        {segments.map(s => (
          <circle key={s.key} cx="50" cy="50" r={r} fill="none"
            stroke={colors[s.key.toLowerCase()] ?? '#888'}
            strokeWidth="16"
            strokeDasharray={`${s.dash} ${circ - s.dash}`}
            strokeDashoffset={-s.offset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50px 50px' }} />
        ))}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {segments.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: colors[s.key.toLowerCase()] ?? '#888', flexShrink: 0 }} />
            <span style={{ color: 'var(--text-2)' }}>{s.key}</span>
            <span style={{ fontWeight: 600, color: 'var(--navy)', marginLeft: 'auto', paddingLeft: 12 }}>{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FunnelChart({ data }: { data: Record<string, number> }) {
  const maxVal = Math.max(...Object.values(data), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Object.entries(data).map(([stage, count]) => (
        <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ minWidth: 90, fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{stage}</span>
          <div style={{ flex: 1, height: 18, background: '#F0F0F0', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${Math.max((count / maxVal) * 100, 4)}%`,
              background: 'var(--primary)',
              borderRadius: 4,
              transition: 'width .4s ease',
            }} />
          </div>
          <span style={{ fontSize: 12, color: '#888', minWidth: 28, textAlign: 'right' }}>{count}</span>
        </div>
      ))}
    </div>
  );
}

const SOURCE_COLORS: Record<string, string> = {
  meta: '#1877F2', google: '#4285F4', indiamart: '#E87722', justdial: '#E4222A',
  referral: '#1D9E75', manual: '#888', whatsapp: '#25D366', website: '#534AB7', other: '#aaa',
};

// Deal-stage colors (Phase 4, four-axis lead status model) — must match
// the LeadStatusBadge palette exactly.
const DEAL_STAGE_COLORS: Record<string, string> = {
  new:         '#94a3b8',
  contacted:   '#3b82f6',
  qualified:   '#6366f1',
  quoted:      '#06b6d4',
  negotiation: '#f59e0b',
  nurture:     '#64748b',
  won:         '#22c55e',
  lost:        '#ef4444',
};

const DEAL_STAGE_LABELS_MAP: Record<string, string> = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified', quoted: 'Quoted',
  negotiation: 'Negotiation', nurture: 'Nurture', won: 'Won', lost: 'Lost',
};

// ── Date range picker (matches CallReportsPage pattern) ─────────────────────
type Preset = 'today' | 'yesterday' | 'last7' | 'last14' | 'last30' | 'custom';

const ymd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const resolveRange = (preset: Preset, dateFrom: string, dateTo: string): { from: string; to: string } => {
  const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  const back = (n: number) => { const d = new Date(today); d.setDate(today.getDate() - n); return d; };
  switch (preset) {
    case 'today':     return { from: ymd(today),   to: ymd(today) };
    case 'yesterday': return { from: ymd(y),       to: ymd(y) };
    case 'last7':     return { from: ymd(back(6)), to: ymd(today) };
    case 'last14':    return { from: ymd(back(13)),to: ymd(today) };
    case 'last30':    return { from: ymd(back(29)),to: ymd(today) };
    case 'custom':    return { from: dateFrom,     to: dateTo };
  }
};

// ── Small snapshot card (Today / Yesterday) ─────────────────────────────────
const SnapCard: React.FC<{
  label: string; value: number | string; sub?: string; accent?: string; delta?: number; onClick?: () => void;
}> = ({ label, value, sub, accent, delta, onClick }) => (
  <div className="kpi-card" onClick={onClick}
    style={{ borderLeft: `4px solid ${accent ?? 'var(--primary)'}`, cursor: onClick ? 'pointer' : undefined }}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-value">{value}</div>
    {typeof delta === 'number' && <Delta val={delta} suffix="vs yesterday" />}
    {sub && !delta && <div className="kpi-delta" style={{ color: 'var(--text-muted)' }}>{sub}</div>}
  </div>
);

const Dashboard: React.FC = () => {
  const [stats, setStats]         = useState<DashboardStats | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [preset, setPreset]       = useState<Preset>('last7');
  const [dateFrom, setDateFrom]   = useState<string>(ymd(new Date(Date.now() - 6 * 86400 * 1000)));
  const [dateTo, setDateTo]       = useState<string>(ymd(new Date()));
  const [agentId, setAgentId]     = useState('');
  const [drill, setDrill]         = useState<{ metric: string; label: string } | null>(null);

  const range = useMemo(() => resolveRange(preset, dateFrom, dateTo), [preset, dateFrom, dateTo]);

  const load = () => {
    setLoading(true); setError(null);
    fetchDashboardStats({
      date_from: range.from,
      date_to:   range.to,
      agent_id:  agentId || undefined,
    })
      .then(setStats)
      .catch(() => setError('Failed to load dashboard stats'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range.from, range.to, agentId]);

  const handlePreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const r = resolveRange(p, dateFrom, dateTo);
      setDateFrom(r.from);
      setDateTo(r.to);
    }
  };

  if (loading) return <div className="spinner">Loading&hellip;</div>;
  if (error)   return <div className="alert alert-error">{error}</div>;
  if (!stats)  return null;

  return (
    <div className="dashboard-page">
      {/* ── Header ── */}
      <div className="page-hd">
        <h1 className="page-title">Dashboard</h1>
        <div className="page-hd-right" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="filter-select" value={preset} onChange={e => handlePreset(e.target.value as Preset)}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="last14">Last 14 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          {preset === 'custom' && (
            <>
              <input
                type="date"
                className="filter-select"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={e => setDateFrom(e.target.value)}
              />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
              <input
                type="date"
                className="filter-select"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={e => setDateTo(e.target.value)}
              />
            </>
          )}
          <select className="filter-select" value={agentId} onChange={e => setAgentId(e.target.value)}>
            <option value="">All agents</option>
            {(stats.agents ?? []).map((a: User) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>

      {/* ── Applied range echo ── */}
      {stats.range && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Showing <strong>{stats.range.from}</strong> to <strong>{stats.range.to}</strong>
        </div>
      )}

      {/* ── Today Snapshot (2026-07-20) — always current-day, ignores range filter ── */}
      {stats.today && (
        <>
          <div style={{ margin: '4px 0 10px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Today Snapshot
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              Always shows literal today / yesterday (IST) — does not change with the date filter above.
            </div>
          </div>
          <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
            <SnapCard
              label="Leads Today"
              value={stats.today.leads_today}
              accent="#7c3aed"
              delta={stats.today.delta_leads_today}
              onClick={() => setDrill({ metric: 'leads_today', label: 'Leads Today' })}
            />
            <SnapCard
              label="Leads Yesterday"
              value={stats.today.leads_yesterday}
              accent="#94a3b8"
              sub={`${stats.today.leads_yesterday} created yesterday`}
              onClick={() => setDrill({ metric: 'leads_yesterday', label: 'Leads Yesterday' })}
            />
            <SnapCard
              label="Calls Today"
              value={stats.today.calls_today}
              accent="#3b82f6"
              sub={`${stats.today.calls_connected_today} connected`}
              onClick={() => setDrill({ metric: 'calls_today', label: 'Calls Today' })}
            />
            <SnapCard
              label="Auto-Tasks Today"
              value={stats.today.auto_tasks_today}
              accent="#22c55e"
              sub="From lead-status automations"
              onClick={() => setDrill({ metric: 'auto_tasks_today', label: 'Auto-Tasks Today' })}
            />
          </div>
        </>
      )}

      {/* ── Period KPI Cards ── */}
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', margin: '4px 0 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Period Overview
      </div>
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'leads_created', label: 'Leads Created' })}>
          <div className="kpi-label">Leads Created</div>
          <div className="kpi-value">{stats.leads_created}</div>
          <Delta val={stats.delta_leads_created} />
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'leads_qualified', label: 'Leads Qualified' })}>
          <div className="kpi-label">Leads Qualified</div>
          <div className="kpi-value">{stats.leads_qualified}</div>
          <Delta val={stats.delta_leads_qualified} />
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'conversion_rate', label: 'Conversion Rate — Qualified Leads' })}>
          <div className="kpi-label">Conversion Rate</div>
          <div className="kpi-value">{(stats.conversion_rate ?? 0).toFixed(1)}%</div>
          <div className="kpi-delta" style={{ color: 'var(--text-muted)' }}>Qualified ÷ Created</div>
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'deals_won', label: 'Deals Won' })}>
          <div className="kpi-label">Deals Won</div>
          <div className="kpi-value">{stats.deals_won}</div>
          <Delta val={stats.delta_deals_won} />
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'deals_lost', label: 'Deals Lost' })}>
          <div className="kpi-label">Deals Lost</div>
          <div className="kpi-value">{stats.deals_lost}</div>
          <Delta val={stats.delta_deals_lost} invert />
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'revenue_won', label: 'Revenue Won — Won Deals' })}>
          <div className="kpi-label">Revenue Won</div>
          <div className="kpi-value" style={{ fontSize: 22 }}>{fmt(stats.revenue_won)}</div>
          <Delta val={stats.delta_revenue_won} />
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'avg_deal_size', label: 'Avg Deal Size — Won Deals' })}>
          <div className="kpi-label">Avg Deal Size</div>
          <div className="kpi-value" style={{ fontSize: 22 }}>{fmt(stats.avg_deal_size ?? 0)}</div>
          <div className="kpi-delta" style={{ color: 'var(--text-muted)' }}>Won revenue ÷ Won deals</div>
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'forecast_weighted', label: 'Forecast — Open Deals' })}>
          <div className="kpi-label">Forecast (Weighted)</div>
          <div className="kpi-value" style={{ fontSize: 22, color: 'var(--primary)' }}>{fmt(stats.forecast_weighted)}</div>
          <div className="kpi-delta" style={{ color: 'var(--text-muted)' }}>Open deals × probability</div>
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'hot_leads', label: 'Hot Leads' })}>
          <div className="kpi-label">Hot Leads</div>
          <div className="kpi-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {stats.hot_leads}
            <span className="hot-dot" />
          </div>
          <div className="kpi-delta kpi-delta-up">+{stats.hot_leads} this period</div>
        </div>
        <div className="kpi-card kpi-card-warning" style={{ cursor: 'pointer' }} onClick={() => setDrill({ metric: 'tasks_overdue', label: 'Tasks Overdue' })}>
          <div className="kpi-label">Tasks Overdue</div>
          <div className="kpi-value" style={{ color: 'var(--coral)' }}>{stats.tasks_overdue}</div>
          {stats.tasks_overdue > 0 && <div className="kpi-delta kpi-delta-down">Needs attention</div>}
        </div>
      </div>

      {/* ── Charts ── */}
      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-title">Leads by Source</div>
          {Object.keys(stats.leads_by_source ?? {}).length > 0
            ? <DonutChart data={stats.leads_by_source} colors={SOURCE_COLORS} />
            : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No lead data yet</p>}
        </div>

        <div className="chart-card">
          <div className="chart-title">Qualification Funnel</div>
          {Object.keys(stats.qualification_funnel ?? {}).length > 0
            ? <FunnelChart data={stats.qualification_funnel} />
            : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data yet</p>}
        </div>
      </div>

      {/* Deal-stage distribution (Phase 4, four-axis lead status model) */}
      {stats.leads_by_deal_stage && Object.values(stats.leads_by_deal_stage).some(v => v > 0) && (
        <div className="charts-grid">
          <div className="chart-card" style={{ gridColumn: '1 / -1' }}>
            <div className="chart-title">Deal Stage Distribution</div>
            <DonutChart
              data={Object.fromEntries(
                Object.entries(stats.leads_by_deal_stage)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => [DEAL_STAGE_LABELS_MAP[k] ?? k, v])
              )}
              colors={Object.fromEntries(
                Object.entries(DEAL_STAGE_COLORS).map(([k, c]) =>
                  [(DEAL_STAGE_LABELS_MAP[k] ?? k).toLowerCase(), c]
                )
              )}
            />
          </div>
        </div>
      )}

      {/* ── Recent tables ── */}
      <div className="charts-grid">
        <div className="data-card">
          <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border)', fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>
            Recent Leads
          </div>
          <div className="data-card-wrap">
            <table>
              <thead><tr><th>Name</th><th>Status</th><th>Source</th></tr></thead>
              <tbody>
                {stats.recent_leads.length > 0 ? stats.recent_leads.map(l => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 500 }}>{l.name}</td>
                    <td><span className={`badge badge-${l.status}`}>{l.status}</span></td>
                    <td className="td-muted">{l.source}</td>
                  </tr>
                )) : <tr className="table-empty-row"><td colSpan={3}>No leads yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="data-card">
          <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border)', fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>
            Recent Deals
          </div>
          <div className="data-card-wrap">
            <table>
              <thead><tr><th>Title</th><th>Value</th><th>Stage</th></tr></thead>
              <tbody>
                {(stats.recent_deals ?? []).length > 0 ? stats.recent_deals.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500 }}>{d.title}</td>
                    <td style={{ color: 'var(--teal)', fontWeight: 600 }}>{fmt(Number(d.value))}</td>
                    <td><span className={`badge stage-${d.stage}`}>{d.stage.replace('_', ' ')}</span></td>
                  </tr>
                )) : <tr className="table-empty-row"><td colSpan={3}>No deals yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {drill && (
        <DrilldownModal
          metric={drill.metric}
          label={drill.label}
          params={{ date_from: range.from, date_to: range.to, agent_id: agentId || undefined }}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
